// @flow
/* eslint-disable */
import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import Card from '../../components/Card';
import styles from './styles.module.scss';
import { Button, H4, InputSearch } from '../../components';
import { useSelector } from 'react-redux';
import Toggler from '../../components/Toggler';
import Select from '../../components/Select';
import GameResult from './gameResult';
import Preloader from 'components/Preloader';
import SimilarPercentages from './similarPercentages';
import SessionStatistics from '../TrainingGame/ResultStatistics/SessionStatistics';
import {
  getPostflopMaxSearchLength,
  hasDuplicateSuitedCards,
  normalizePostflopSearchInput,
  shouldRequestPostflopSimilar,
} from '../../utils/postflopHand';

type Props = {
  searchValue: string,
  setSearchValue: () => void,
  syncCardsFromSearch: () => void,
  resultCategory: any,
  setResultCategory: () => void,
  makeSimilarRequest: () => void,
  setNonSuitCardSelected: () => void,
  selectedBoard: any,
  selectedCards: any,
  currentStep: any,
  clearCards: () => void,
  isPlo5?: boolean,
  pageMode: any,
  rightResults: number,
  wrongResults: number,
  allResults: number,
  resetTrainingResults: () => void,
  actionContext?: any,
  strategyUnavailable?: boolean,
  statusMessage?: string,
};

const drawFilters = [
  { label: 'ALL', value: 'all' },
  { label: 'MADE', value: 'made' },
  { label: 'DRAW', value: 'draw' },
]

const resultCategoriesOptionsAll = [
  {label: '1 PAIR', value: 'one_pair'},
  {label: "2 PAIRS", value: 'two_pair'},
  {label: "TRIPS+", value: 'trips'},
  {label: "STRAIGHT", value: 'straight'},
  {label: "FLUSH", value: 'flush'},
  {label: "SD", value: 'sd'},
  {label: "FD", value: 'fd'},
  {label: "BDFD", value: 'bdfd'},
]

const resultCategoriesOptionsMade = [
  {label: '1 PAIR', value: 'one_pair'},
  {label: "2 PAIRS", value: 'two_pair'},
  {label: "TRIPS+", value: 'trips'},
  {label: "STRAIGHT", value: 'straight'},
  {label: "FLUSH", value: 'flush'}
]

const resultCategoriesOptionsDraw = [
  {label: "SD", value: 'sd'},
  {label: "FD", value: 'fd'},
  {label: "BDFD", value: 'bdfd'},
]

const ResultPanel = ({
  searchValue,
  setSearchValue,
  syncCardsFromSearch,
  resultCategory,
  setResultCategory,
  makeSimilarRequest,
  selectedBoard,
  selectedCards,
  currentStep,
  setNonSuitCardSelected,
  clearCards,
  isPlo5,
  pageMode,
  rightResults,
  wrongResults,
  allResults,
  resetTrainingResults,
  actionContext = {},
  strategyUnavailable = false,
  statusMessage = '',
}: Props) => {
  const [drawFilter, setDrawFilter] = useState({label: 'ALL', value: 'all'})
  const [sortAction, setSortAction] = useState(null);

  const similarResponse = useSelector((state) => state.postflop.similarResponse);
  const isLoadingSimilar = useSelector((state) => state?.api?.SET_POSTFLOP_SIMILAR?.loading);
  const similarError = useSelector((state) => state?.api?.SET_POSTFLOP_SIMILAR?.error);
  const currentCombo = useSelector((state) => state?.postflop?.combo);
  useEffect(() => {
    setSortAction(null);
  }, [similarResponse]);

  const retryFromSearch = ['Training', 'Results'].includes(pageMode?.value);
  const retryHand = retryFromSearch && !selectedCards?.length
    ? currentCombo?.combo || searchValue : searchValue;

  const availableCategories = () => {
    const cat = similarResponse?.percentage?.map((el) => (
      el.category
    ))
    return cat
  }

  const resultCategoryHandler = () => {
    if (drawFilter.value === 'all') {
      return resultCategoriesOptionsAll
    } else if (drawFilter.value === 'made') {
      return resultCategoriesOptionsMade
    } else {
      return resultCategoriesOptionsDraw
    }
  }

  const handleDrawFilterChange = (obj) => {
    if (obj.value === 'all') {
      setResultCategory({label: 'All', value: 'all'})
    }
    setDrawFilter(obj)
  }

  useEffect(() => {
    if (!similarResponse?.percentage){
      return
    }
    if (drawFilter.value === 'all') {
      setResultCategory({label: 'All', value: 'all'})
    } else if (drawFilter.value === 'made') {
      const vals = resultCategoriesOptionsMade.map(el => el.value)
      const availableVals = availableCategories()
      const match = vals.find(item => availableVals?.includes(item));
      const res = resultCategoriesOptionsMade.find(item => item?.value === match)
      setResultCategory(res || {label: 'All', value: 'all'})
    } else {
      const vals = resultCategoriesOptionsDraw.map(el => el.value)
      const availableVals = availableCategories()
      const match = vals.find(item => availableVals?.includes(item));
      const res = resultCategoriesOptionsDraw.find(item => item?.value === match)
      setResultCategory(res || {label: 'All', value: 'all'})
    }
  }, [drawFilter, similarResponse])

  const handleInputSearch = (e) => {
    if (!e) {
      setSearchValue('');
      syncCardsFromSearch('');
      return;
    }
    const raw = e.target.value || '';
    if (raw.length > getPostflopMaxSearchLength(isPlo5)) return;

    const transformed = normalizePostflopSearchInput(raw);

    if (hasDuplicateSuitedCards(transformed)) {
      return
    }

    setSearchValue(transformed);
    syncCardsFromSearch(transformed);
    makeSimilarRequest(currentStep, transformed);
  };

  const filteredSimilar = () => {
    const hands = similarResponse?.similar || [];
    const filteredHands = resultCategory?.value === 'all'
      ? hands : hands.filter(el => el[resultCategory?.value]);

    if (!sortAction) return filteredHands;

    return [...filteredHands].sort((a, b) => (
      (Number(b[sortAction]) || 0) - (Number(a[sortAction]) || 0)
    ));
  };
  const displayedSimilar = filteredSimilar();

  const getHighlighted = () => {
    return similarResponse?.similar?.filter(el => el.highlight)
  }

  const isDisabled = () => {
    return similarResponse?.percentage === null
  }

  const hideEmptyMobilePanel = ['Training', 'Results'].includes(pageMode?.value)
    && !allResults && !searchValue && !isLoadingSimilar && !similarError && !strategyUnavailable
    && !similarResponse?.similar?.length;

  return (
    <Card className={classnames(styles.resultPanelWrapper, {
      [styles.resultPanelEmptyMobile]: hideEmptyMobilePanel,
    })}>
      <SessionStatistics
        rightResults={rightResults}
        allResults={allResults}
        wrongResults={wrongResults}
        onReset={resetTrainingResults}
      />
      {strategyUnavailable ? (statusMessage && <p role="status">{statusMessage}</p>) : <>
      <InputSearch
        value={searchValue}
        onChange={handleInputSearch}
        disabled={selectedBoard?.length === 0}
        onClear={clearCards}
      />
      {
        isLoadingSimilar && <Preloader isLoading />
      }
      {
        !isLoadingSimilar && similarError && (
          <div className={styles.ph16} role="alert">
            <p>Could not load similar hands. Please try again.</p>
            <Button
              use="ghost"
              disabled={!shouldRequestPostflopSimilar(retryHand, isPlo5)}
              onClick={() => makeSimilarRequest(currentStep, retryHand, retryFromSearch)}
            >
              Retry similar hands
            </Button>
          </div>
        )
      }
      {
        !isLoadingSimilar && !similarError && similarResponse && selectedCards.length > 0 && (
          <>
            <div className={classnames(styles.ph16, styles.pt4, styles.pb16)}>
              <Toggler
                selected={drawFilter}
                options={drawFilters}
                onToggle={(e) => isDisabled() ? '' : handleDrawFilterChange(e)}
                className={classnames(styles.gameTogglerColor, styles.firstToggler)}
                disabled={isDisabled()}
              />
              <Select
                className={styles.adaptiveSelectComponent}
                value={drawFilter}
                options={drawFilters}
                onChange={(e) => isDisabled() ? '' : handleDrawFilterChange(e)}
                disabled={isDisabled()}
              />
            </div>
            <div className={classnames(styles.controlPanelPosition, styles.control, styles.ph16, styles.pb16)}>
              <Toggler
                selected={resultCategory || {}}
                options={resultCategoryHandler()}
                onToggle={(e) => isDisabled() ? '' : setResultCategory(e)}
                className={styles.gameTogglerColor}
                controlToggler
                is2rowsToggler={drawFilter.value === 'all'}
                forPostflop
                postflopOptions={availableCategories()}
                disabled={isDisabled()}
              />
            </div>
            {
              similarResponse?.percentage?.find((item) => item.category?.toLowerCase() === resultCategory?.value) &&
                <div className={classnames(styles.controlPanelPosition, styles.control, styles.pb16)}>
                  <SimilarPercentages
                    percentage={similarResponse?.percentage?.find((item) => item.category?.toLowerCase() === resultCategory?.value)}
                    actionContext={actionContext}
                    onSort={setSortAction}
                    sortAction={sortAction}
                  />
                </div>
            }
            <div className={classnames(styles.controlPanelPosition, styles.control, styles.pb16)}>
              {getHighlighted()?.length > 0 && getHighlighted()?.map(el => (
                <GameResult
                  key={'highlighted-' + el.combo}
                  combo={el.combo}
                  highlight={el.highlight}
                  bet={el.bet}
                  small={el.small}
                  big={el.big}
                  call={el.call}
                  check={el.check}
                  raise={el.raise}
                  fold={el.fold}
                  isPlo5={isPlo5}
                  actionContext={actionContext}
                />
              ))}
            </div>
            <div className={styles.resultsWrapper}>
              <div style={{ width: '100%' }}>
                {displayedSimilar.length > 0 ? displayedSimilar.map(el => (
                  <GameResult
                    key={el.combo}
                    combo={el.combo}
                    highlight={el.highlight}
                    bet={el.bet}
                    small={el.small}
                    big={el.big}
                    call={el.call}
                    check={el.check}
                    raise={el.raise}
                    fold={el.fold}
                    isPlo5={isPlo5}
                    actionContext={actionContext}
                  />
                )) : <div className={styles.noHandsSpan}><H4>No Hands Available</H4></div>}
              </div>
            </div>
          </>
        )
      }
      </>}
    </Card>
  )
};

export default ResultPanel;
